import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import styled, { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import DonutChart from 'common/charts/GoogleCharts/DonutChart';
import BarChart from 'common/charts/GoogleCharts/BarChart';
import LineChart from 'common/charts/GoogleCharts/LineChart';
import LoadingSvg from 'common/images/LoadingSvg';
import BigNumber from 'common/charts/BigNumber';
import CountBar from 'common/charts/CountBar';
import Metrics from 'common/charts/Metrics';
import NoDataWidget from 'common/charts/NoDataWidget';
import { getHighestPrivilegeRole } from 'common/rbac/util';
import { bindActionCreators } from 'redux';
import { widgetActions } from './actions/widget.actions';

const Loader = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

function Widget({
  height,
  width,
  metadata,
  showBorder,
  widgetActions,
  gridColumn,
  filters,
  filterRoles,
  area_id,
  machine_id,
  widgetInterval,
  setTimeInterval,
  showToolbarOverlay,
  user,
  widgetClassName,
  setWidgetLoading, // for parent component to keep track of loading
  setFilterOptions,
  selectedUserTeams,
  onDelete,
  widgetId,
  currentAccount,
  onEmptyStateChange,
  admindashboard,
  organisationId,
  widgetsMetadata,
  ...props
}) {
  const { t } = useTranslation();
  const [dataLoading, setDataLoading] = useState(false);
  const [errorType, setErrorType] = useState(null);
  const [widgetProps, setWidgetProps] = useState({
    data: null
  });
  const isFetchingRef = useRef(false);
  // Set when a fetch is requested while one is already in flight, so the newer
  // request is re-run on completion instead of being silently dropped.
  const pendingFetchRef = useRef(false);
  // Always points at the newest fetchChartData, so a queued re-run uses latest args.
  const fetchChartDataRef = useRef();
  const isMountedRef = useRef(true);
  const lastFiltersRef = useRef();
  const lastFetchedCurrencyRef = useRef();
  const lastMetadataRef = useRef();

  const [localWidgetInterval, setLocalWidgetInterval] = useState(widgetInterval);
  const [localCustomDayFrom, setLocalCustomDayFrom] = useState(null);

  const handleSetTimeInterval = useCallback((value, customDate) => {
    if (typeof value === 'number' && value !== 365 && value !== 730 && value !== 1095) {
      setLocalWidgetInterval('custom');
      setLocalCustomDayFrom(customDate || new Date(Date.now() - value * 24 * 60 * 60 * 1000));
    } else {
      setLocalWidgetInterval(value);
      setLocalCustomDayFrom(null);
    }
    if (setTimeInterval) {
      setTimeInterval(value);
    }
  }, [setTimeInterval]);

  // Keep a stable filters reference; parent pages sometimes create new filter objects/arrays
  // every render, which would otherwise retrigger the fetch effect on identical inputs.
  const stableFilters = React.useMemo(() => {
    if (lastFiltersRef.current && _.isEqual(filters, lastFiltersRef.current)) {
      return lastFiltersRef.current;
    }
    lastFiltersRef.current = filters;
    return filters;
  }, [filters]);

  // Same guard for `metadata`. Note `_.isEqual` compares functions by reference, and
  // widget metadata carries render props (`component`, `handleClick`, `tableMetadata`),
  // so this only collapses identities when those are already referentially stable —
  // which they are today: every call site passes `widgetsMetadata.object.X` straight
  // from Redux, or a memoized object. It does NOT make metadata built with inline
  // closures during render safe; such a caller would still refetch every render.
  const stableMetadata = React.useMemo(() => {
    if (lastMetadataRef.current && _.isEqual(metadata, lastMetadataRef.current)) {
      return lastMetadataRef.current;
    }
    lastMetadataRef.current = metadata;
    return metadata;
  }, [metadata]);

  // Read through the memoized reference everywhere, so the fetch path and the render
  // path can never disagree about which object they are looking at.
  const { id, chart_type: type, name: widgetName } = stableMetadata || {};
  const Component = stableMetadata && stableMetadata.component ? stableMetadata.component() : null;
  const isOrgSelfLoadedGraphics =
    organisationId != null && type === 'graphics' && !!Component;

  const fetchChartData = useCallback(async () => {
    // Org self-loaded graphics render their own data; nothing to fetch.
    if (!stableMetadata || isOrgSelfLoadedGraphics) return;
    // Site widgets need an id; org widgets can fetch by (organisationId, widgetName).
    if (!id && !(organisationId != null && widgetName)) return;
    // A fetch is already in flight: queue this one instead of dropping it, so the
    // newest request still runs once the current one settles.
    if (isFetchingRef.current) { pendingFetchRef.current = true; return; }
    // Everything below sits inside the try: `finally` is what releases isFetchingRef
    // and drains pendingFetchRef, so a throw during setup must not skip it — otherwise
    // the widget wedges in a permanent loading state that nothing can recover from.
    try {
      isFetchingRef.current = true;
      setDataLoading(true);
      setWidgetLoading(true);
      setErrorType(null);
      const params = {};
      if (id != null && !Number.isNaN(Number(id))) {
        params.widget_id = id;
      }
      if (organisationId != null) {
        params.organisation_id = organisationId;
      }
      if (!params.widget_id && widgetName) {
        params.widget_name = widgetName;
      }
      if (!_.isEmpty(stableFilters)) {
        if (
          _.isEmpty(filterRoles) ||
          (user &&
            user.roles &&
            filterRoles &&
            filterRoles.includes(getHighestPrivilegeRole(user.roles)))
        ) {
          params.filters = [...stableFilters];
        }
      }
      if (area_id !== undefined) {
        if (!params.filters) params.filters = [];
        params.filters.push({ name: 'area_id', op: 'eq', value: area_id });
      }
      if (machine_id !== undefined) {
        params.machine_id = machine_id;
      }
      if (!_.isEmpty(selectedUserTeams)) {
        if (!params.filters) params.filters = [];
        params.filters.push({ name: 'team', op: 'in', value: selectedUserTeams });
      }
      // Health timeline widget - always allow interval change (bypasses allow_interval_change requirement)
      const isHealthTimeline = id === 'health_timeline' || (widgetId && widgetId.startsWith('health_timeline'));
      if (isHealthTimeline && localWidgetInterval && localWidgetInterval !== 'all') {
        params.end_time = moment().toDate();
        if (localWidgetInterval === 'custom' && localCustomDayFrom) {
          params.start_time = moment(localCustomDayFrom).startOf('day').toDate();
        } else if (typeof localWidgetInterval === 'number') {
          params.start_time = moment().subtract(localWidgetInterval, 'days').startOf('day').toDate();
        }
      } else if (stableMetadata.widget_metadata && stableMetadata.widget_metadata.allow_interval_change && widgetInterval && widgetInterval !== 'all') {
        params.end_time = moment().toDate();
        params.start_time = moment().subtract(widgetInterval, 'days').startOf('day').toDate();
      }
      if (stableMetadata.widget_metadata && stableMetadata.widget_metadata.page) {
        params.page = stableMetadata.widget_metadata.page;
      }
      if (stableMetadata.widget_metadata && stableMetadata.widget_metadata.per_page) {
        params.per_page = stableMetadata.widget_metadata.per_page;
      }
      const [data, filterOptions, rawData] = await widgetActions.getWidgetData(params, stableMetadata, type);
      if (!_.isEmpty(filterOptions) && stableMetadata.filterName) {
        setFilterOptions(stableMetadata.filterName, filterOptions);
      }
      setWidgetProps(prevProps => ({
        ...prevProps,
        data,
        tableData: admindashboard && rawData ? rawData : null,
        handleClick: (organisationId == null && stableMetadata.handleClick) ?
          stableMetadata.handleClick(stableMetadata.name, filterOptions, machine_id, admindashboard) :
          () => {},
        tableMetadata: stableMetadata.tableMetadata || null
      }));
      setDataLoading(false);
      setWidgetLoading(false);
    } catch (error) {
      if (typeof error !== 'string' && error.code === 404) {
        setErrorType('404');
      } else {
        setErrorType('Other');
      }
      setDataLoading(false);
      setWidgetLoading(false);
    } finally {
      isFetchingRef.current = false;
      // A newer request arrived mid-flight (e.g. a language switch changed the
      // translated tableMetadata); re-run the latest one so it isn't lost. Skip it if
      // the widget unmounted meanwhile, or we'd setState on a dead component and fire
      // a request nobody is waiting for.
      if (pendingFetchRef.current) {
        pendingFetchRef.current = false;
        if (isMountedRef.current && fetchChartDataRef.current) {
          Promise.resolve(fetchChartDataRef.current()).catch(() => {});
        }
      }
    }
  }, [id, widgetId, widgetName, stableFilters, filterRoles, user, area_id, machine_id, organisationId, isOrgSelfLoadedGraphics, selectedUserTeams, widgetInterval, localWidgetInterval, localCustomDayFrom, stableMetadata, type, widgetActions, setWidgetLoading, setFilterOptions, currentAccount]);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    fetchChartDataRef.current = fetchChartData;
  }, [fetchChartData]);

  useEffect(() => {
    if (!stableMetadata || !stableMetadata.refresh_rate) return undefined;
    const interval = setInterval(fetchChartData, stableMetadata.refresh_rate * 1000);
    return () => clearInterval(interval);
  }, [fetchChartData, stableMetadata && stableMetadata.refresh_rate]);

  useEffect(() => {
    if (!isOrgSelfLoadedGraphics) {
      fetchChartData();
    } else {
      setDataLoading(false);
      setWidgetLoading(false);
    }
  }, [fetchChartData, isOrgSelfLoadedGraphics, setWidgetLoading]);

  // Re-fetch widget data when currency preference changes (for cost saved widgets)
  const currency = currentAccount && currentAccount.preferences && currentAccount.preferences.currency;
  const isCostSavedWidget = metadata && (metadata.name === 'total_cost_saved' || metadata.id === 'total_cost_saved' || metadata.name === 'cost_saved_by_machine');
  useEffect(() => {
    if (!isCostSavedWidget || !currency) return;
    // Only depend on the currency string (a stable primitive), not the whole
    // metadata/fetchChartData identity - those can be rebuilt by unrelated
    // renders and would otherwise cause repeated fetches on every such render.
    if (lastFetchedCurrencyRef.current === currency) return;
    lastFetchedCurrencyRef.current = currency;
    if (!isFetchingRef.current) {
      fetchChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, isCostSavedWidget]);

  if (!metadata) {
    return null;
  }

  const renderWidget = () => {
    const allowNavigate = organisationId == null;
    const interactiveProps = {
      isInteractive: allowNavigate && !!metadata.isInteractive,
      handleClick: allowNavigate ? widgetProps.handleClick : () => {}
    };

    switch (type) {
      case 'list':
      case 'timeline':
      case 'table':
      case 'graphics':
        if (!Component) return <></>;
        return (
          <Component
            data={widgetProps.data}
            machineId={machine_id}
            organisationId={organisationId}
            widgetsMetadata={widgetsMetadata}
            title={metadata.title}
            handleClick={interactiveProps.handleClick}
            widgetInterval={localWidgetInterval}
            customDayFrom={localCustomDayFrom}
            setTimeInterval={handleSetTimeInterval}
            showToolbarOverlay={showToolbarOverlay}
            onDelete={onDelete}
            widgetId={widgetId}
            onEmptyStateChange={onEmptyStateChange}
          />
        );
      case 'donut':
        return <DonutChart {...metadata} {...widgetProps} {...interactiveProps} />;
      case 'bar':
        return <BarChart {...metadata} {...widgetProps} {...interactiveProps} />;
      case 'trend':
        return <LineChart {...metadata} {...widgetProps} {...interactiveProps} />;
      case 'count':
        return (
          <BigNumber
            {...metadata}
            {...widgetProps}
            {...interactiveProps}
            admindashboard={admindashboard}
          />
        );
      case 'count/bar':
        return <CountBar {...metadata} {...widgetProps} {...interactiveProps} />;
      case 'metrics':
        return <Metrics {...metadata} {...widgetProps} {...interactiveProps} />;
      default:
        return <></>;
    }
  };

  if (!dataLoading && !errorType && type === 'table' && (!widgetProps.data || !Array.isArray(widgetProps.data.items) || widgetProps.data.items.length === 0)) {
    return null;
  }
  return (
    <div
      className={widgetClassName}
      style={{
        height,
        width,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '4px',
        overflow: 'hidden',
        border: showBorder ? `1px solid ${props.theme.colors.borderGray}` : '',
        gridColumn: gridColumn || ''
      }}
    >
      {dataLoading && (
        <Loader>
          <LoadingSvg />
        </Loader>
      )}
      {!dataLoading && errorType && (
        <NoDataWidget
          title={metadata && metadata.title}
          message={errorType === '404' ? t('widgetLibrary.errors.noDataAvailable') : t('widgetLibrary.errors.errorFetchingData')}
        />
      )}
      {!dataLoading && !errorType &&
        (isOrgSelfLoadedGraphics || widgetProps.data) && renderWidget()}
    </div>
  );
}

Widget.propTypes = {
  height: PropTypes.string,
  width: PropTypes.string,
  showBorder: PropTypes.bool,
  filters: PropTypes.object,
  filterRoles: PropTypes.array,
  widgetClassName: PropTypes.string,
  setWidgetLoading: PropTypes.func,
  setFilterOptions: PropTypes.func,
  selectedUserTeams: PropTypes.array,
  machine_id: PropTypes.number,
  widgetInterval: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setTimeInterval: PropTypes.func,
  showToolbarOverlay: PropTypes.bool,
  onDelete: PropTypes.func,
  widgetId: PropTypes.string,
  onEmptyStateChange: PropTypes.func,
  admindashboard: PropTypes.bool
};

Widget.defaultProps = {
  height: '100%',
  width: '100%',
  showBorder: false,
  filters: {},
  filterRoles: [],
  widgetClassName: '',
  setWidgetLoading: () => {},
  setFilterOptions: () => {},
  selectedUserTeams: [],
  machine_id: undefined,
  widgetInterval: undefined,
  setTimeInterval: null,
  showToolbarOverlay: false,
  onDelete: null,
  widgetId: '',
  onEmptyStateChange: null,
  admindashboard: false
};

const mapDispatchToProps = dispatch => ({
  widgetActions: bindActionCreators(widgetActions, dispatch),
});

const mapStateToProps = state => ({
  user: state.user.user,
  currentAccount: state.currentAccount
});

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(Widget));
